import { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { IEmailTemplate } from "../../../App/Typings/MessageText";
import { useUpsertMessageTemplate } from "../../../App/Queries/MessageQueries";
import { InputEmailBody, InputName, InputSubject, InputVisibility } from "../Shared/TemplateInputs";
import { selectDraftModalState, useDraftMessageTemplateStore } from "../Shared/useMessageTemplateStore";

export const DraftEmailTemplateModal = ({
  isDraftDialogOpen,
  setIsDraftDialogOpen,
  action,
  editTemplate,
  setEditTemplate,
}: {
  isDraftDialogOpen: boolean;
  setIsDraftDialogOpen: (value: boolean) => void;
  action: "create" | "edit" | "duplicate";
  editTemplate: IEmailTemplate | null;
  setEditTemplate: (value: IEmailTemplate | null) => void;
}) => {
  const handleCloseModal = () => {
    setIsDraftDialogOpen(false);
  };
  return (
    <Dialog
      open={isDraftDialogOpen}
      fullWidth
      maxWidth="lg"
      disableEnforceFocus={true}
      onClose={handleCloseModal}
      data-test={`${action}-modal`}
    >
      {isDraftDialogOpen && (
        <DraftEmailTemplateContent
          action={action}
          setIsDraftDialogOpen={setIsDraftDialogOpen}
          editTemplate={editTemplate}
          setEditTemplate={setEditTemplate}
        />
      )}
    </Dialog>
  );
};

const DraftEmailTemplateContent = ({
  action,
  setIsDraftDialogOpen,
  editTemplate,
  setEditTemplate,
}: {
  action: "create" | "edit" | "duplicate";
  setIsDraftDialogOpen: (value: boolean) => void;
  editTemplate: IEmailTemplate | null;
  setEditTemplate: (value: IEmailTemplate | null) => void;
}) => {
  const { disableSaveMessage, clearDraft, createUpsertCommand, createDraft, createDuplicate, setType } =
    useDraftMessageTemplateStore(selectDraftModalState);
  const { mutateAsync: upsertMessageTemplate, isLoading: isLoadingUpsertMessageTemplate } =
    useUpsertMessageTemplate();

  const actionTitle = `${action[0].toUpperCase() + action.slice(1)} Email Template`;
  const actionId = `${action}-email-template`;

  const handleCloseModal = () => {
    setIsDraftDialogOpen(false);
    setTimeout(() => {
      clearDraft();
      setEditTemplate(null);
    }, 300);
  };

  const handleSaveCurrentTemplate = async () => {
    if (!disableSaveMessage) {
      await upsertMessageTemplate(createUpsertCommand());
      handleCloseModal();
    }
  };

  useEffect(() => {
    setType("email");
    if (editTemplate && action === "edit") {
      createDraft(editTemplate);
    } else if (editTemplate && action === "duplicate") {
      createDuplicate(editTemplate);
    } else if (action === "create") {
      createDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <DialogTitle data-test={`${actionId}-title`}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span id={`${actionId}-label`}>{actionTitle}</span>
          <IconButton
            data-test={`close-${actionId}-modal`}
            aria-label="Close Dialog"
            onClick={handleCloseModal}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider sx={{ maxWidth: "calc(100% - 48px)", mx: "auto", my: 0, width: 1 }} />
      <DialogContent sx={{ pt: "1rem" }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, mb: 2 }}>
          <Box sx={{ flex: 2 }}>
            <InputName initialValue={editTemplate?.name ?? ""} isLoading={isLoadingUpsertMessageTemplate} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <InputVisibility isLoading={isLoadingUpsertMessageTemplate} />
          </Box>
        </Box>
        <Box>
          <Box sx={{ mb: 2 }}>
            <InputSubject
              initialValue={editTemplate?.subject ?? ""}
              isLoading={isLoadingUpsertMessageTemplate}
            />
          </Box>
          <Box>
            <InputEmailBody
              initialValue={editTemplate?.body ?? ""}
              isLoading={isLoadingUpsertMessageTemplate}
            />
          </Box>
        </Box>
      </DialogContent>
      <Divider sx={{ maxWidth: "calc(100% - 48px)", mx: "auto", my: 0, width: 1 }} />
      <DialogActions sx={{ paddingRight: "1rem", paddingBottom: "1rem" }}>
        <Tooltip title={disableSaveMessage ?? ""} arrow>
          <Box>
            <Button
              variant="contained"
              id="save-template-button"
              aria-haspopup="true"
              disableElevation
              disabled={!!disableSaveMessage || isLoadingUpsertMessageTemplate}
              data-test="save-template"
              onClick={handleSaveCurrentTemplate}
            >
              Save
            </Button>
          </Box>
        </Tooltip>
      </DialogActions>
    </>
  );
};
